import { $UserDetailsDto, client, UserDetailsDto } from "@/client";
import SelectInput from "@/components/select-input/select-input";
import { cn, mapSelf } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { usePermissions } from "@/hooks/use-permissions";
import { extractPermissionsForRole } from "./-permissions-utils";

const roleMap: Partial<{ [key in UserDetailsDto["role"]]: string }> = {
  Viewer: "Reports Viewer",
  AnnotatorManager: "Annotator Manager",
  SiteManager: "Site Manager",
};

const roleToLabel = (role: UserDetailsDto["role"]) => roleMap[role] ?? role;

const roleToOption = (role: UserDetailsDto["role"]) => ({
  label: roleToLabel(role),
  value: role,
});

const RolePicker_ = () => {
  const account = useQuery(client.users.me.asQueryOptions());
  const {
    overrideState: [override, setOverride],
    roleState: [_selectedRole, setSelectedRole],
  } = usePermissions();

  const options = $UserDetailsDto.properties.role.enum.map(roleToOption);

  const currentRole =
    Object.keys(override ?? {}).length === 0
      ? account.data?.role
      : _selectedRole;
  const value = mapSelf(currentRole, (v) => (v ? roleToOption(v) : undefined));

  const { data: allPermissions } = useQuery(
    client.permissions.getAll.asQueryOptions(),
  );

  return (
    <SelectInput
      classNames={{
        valueContainer: (_s) => cn("min-w-[150px]"),
        control: (_s) => cn("h-9"),
      }}
      options={options}
      value={value!}
      isClearable={false}
      isMulti={false}
      onChange={async (option) => {
        if (option?.value && allPermissions) {
          const role = option.value;
          setSelectedRole(role);

          if (role === "SuperAdmin") {
            setOverride(null);
          } else {
            const rolePermissions = extractPermissionsForRole(
              allPermissions,
              role,
            );
            setOverride(rolePermissions);
          }
        }
      }}
    />
  );
};

export function RolePicker() {
  const account = useQuery(client.users.me.asQueryOptions());
  if (account?.data?.role !== "SuperAdmin") return undefined;
  return <RolePicker_ />;
}
